import React from 'react';
import { usePageSeo } from '../hooks/usePageSeo';
import { Button } from '../components/ui/button';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { articles } from '../data/articles';
import Header from '../components/Header';

function formatDate(iso) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const ArticleList = () => {
  const navigate = useNavigate();

  usePageSeo({
    title: 'Short-Term Rental Policy & Compliance Blog | Civic Rentals LLC',
    description: 'Articles on short-term rental regulation, compliance frameworks, permitting, and government program administration from Civic Rentals LLC.',
    canonical: 'https://civicrentals.com/articles',
    ogTitle: 'Short-Term Rental Policy & Compliance Blog | Civic Rentals LLC',
    ogDescription: 'Articles on short-term rental regulation, compliance frameworks, permitting, and government program administration from Civic Rentals LLC.',
    ogUrl: 'https://civicrentals.com/articles',
    ogImage: 'https://civicrentals.com/favicon.png',
    twitterCard: 'summary',
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#d5e0de' }}>

      <Header active="Articles" />

      <section className="pt-32 pb-12 px-6">
        <div className="container mx-auto max-w-3xl">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="mb-6" data-testid="breadcrumbs">
            <ol className="flex items-center gap-1.5 text-sm text-slate-500">
              <li><Link to="/" className="hover:text-slate-800 transition-colors">Home</Link></li>
              <li><ChevronRight className="h-3.5 w-3.5" /></li>
              <li className="text-slate-700 font-medium">Articles</li>
            </ol>
          </nav>

          <div className="text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">Short-Term Rental Policy &amp; Compliance Blog</h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Practical insights on STR regulation, compliance frameworks, permitting, and government program administration.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20 px-6">
        <div className="container mx-auto max-w-3xl">
          <div className="grid gap-6">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={`/articles/${article.slug}`}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 w-full group block no-underline"
                data-testid={`article-link-${article.slug}`}
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500 mb-2">
                  <time dateTime={article.publishedDate}>Published {formatDate(article.publishedDate)}</time>
                  <span>&middot;</span>
                  <span>By <span className="text-slate-600 font-medium">{article.author}</span></span>
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">{article.title}</h2>
                <p className="text-slate-600 leading-relaxed mb-4">{article.summary}</p>
                <span className="text-blue-600 font-medium text-sm inline-flex items-center gap-1">
                  Read Article <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 text-slate-400 py-12 px-6 border-t border-slate-800">
        <div className="container mx-auto max-w-6xl">
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>&copy; 2026 Civic Rentals LLC. All rights reserved.</p>
            <p className="mt-2 space-x-4">
              <a href="/privacypolicy" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="/websiteTOS" className="hover:text-white transition-colors">Terms of Service</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ArticleList;
